import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BookOpen, Lightbulb, Building2, ListChecks, Loader2, CheckCircle2, XCircle, Zap, Trophy } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireConfetti } from "@/lib/confetti";

export default function ServiceNowTopicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      setResult(null);
      setAnswers({});
      try {
        const { data } = await api.get(`/servicenow/extended-library/${slug}`);
        setTopic(data);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load topic");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const payload = { answers: topic.quiz.map((_, i) => parseInt(answers[i], 10)) };
      const { data } = await api.post(`/servicenow/extended-library/${slug}/quiz`, payload);
      setResult(data);
      if (data.passed) {
        if (data.xp_awarded > 0) {
          fireConfetti();
          toast.success(`Topic completed! +${data.xp_awarded} XP, +${data.coins_awarded} coins`);
          refreshUser();
        } else {
          toast.success("Topic completed!");
        }
      } else {
        toast.error(`Scored ${data.score}% — review the feedback and try again.`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit quiz");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 flex justify-center" data-testid="servicenow-topic-page-loading">
        <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
      </div>
    );
  }

  if (error || !topic) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="servicenow-topic-page-error">
        <p className="text-slate-400">{error || "Topic not found"}</p>
        <Link to="/servicenow-curriculum" className="text-emerald-400 text-sm mt-3 inline-block">Back to Curriculum Map</Link>
      </div>
    );
  }

  const allAnswered = topic.quiz.every((_, i) => answers[i] !== undefined);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="servicenow-topic-page">
      <Link to="/servicenow-curriculum" data-testid="servicenow-topic-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Curriculum Map
      </Link>

      <div className="flex items-center gap-3 mb-2">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400">{topic.category} · Topic Library</span>
      </div>
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white">{topic.title}</h1>
      <div className="flex items-center gap-4 mt-3">
        <span className="text-xs font-bold text-[#4ade80] flex items-center gap-1"><Zap className="w-3.5 h-3.5" /> +{topic.xp_reward} XP</span>
        {topic.completed && (
          <span className="text-xs font-bold text-emerald-400 flex items-center gap-1" data-testid="servicenow-topic-completed-badge"><Trophy className="w-3.5 h-3.5" /> Completed{topic.score != null ? ` · ${topic.score}%` : ""}</span>
        )}
      </div>

      <div className="mt-6 space-y-4">
        <div className="glass-panel rounded-2xl p-6" data-testid="servicenow-topic-description">
          <h2 className="font-heading text-sm font-semibold text-white mb-2 flex items-center gap-2"><BookOpen className="w-4 h-4 text-emerald-400" /> Description</h2>
          <p className="text-sm text-slate-300 leading-relaxed">{topic.description}</p>
        </div>

        <div className="glass-panel rounded-2xl p-6" data-testid="servicenow-topic-key-concepts">
          <h2 className="font-heading text-sm font-semibold text-white mb-3 flex items-center gap-2"><Lightbulb className="w-4 h-4 text-emerald-400" /> Key Concepts</h2>
          <ul className="space-y-2.5">
            {topic.key_concepts.map((c, i) => (
              <li key={`${i}-${c.slice(0, 20)}`} className="text-sm text-slate-300 flex gap-2.5">
                <span className="text-emerald-400 font-bold shrink-0">{i + 1}.</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="glass-panel rounded-2xl p-6 border border-emerald-400/20" data-testid="servicenow-topic-scenario">
          <h2 className="font-heading text-sm font-semibold text-white mb-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-400" /> Meridian Health Network Scenario</h2>
          <p className="text-sm text-slate-300 leading-relaxed italic">{topic.scenario}</p>
        </div>

        <div className="glass-panel rounded-2xl p-6" data-testid="servicenow-topic-quiz">
          <h2 className="font-heading text-sm font-semibold text-white mb-4 flex items-center gap-2"><ListChecks className="w-4 h-4 text-emerald-400" /> Knowledge Check</h2>
          <div className="space-y-5">
            {topic.quiz.map((q, i) => {
              const fb = result?.feedback?.find((f) => f.index === i);
              return (
                <div key={`${i}-${q.q.slice(0, 20)}`} data-testid={`servicenow-topic-quiz-q-${i}`}>
                  <p className="text-sm font-medium text-white mb-2">{i + 1}. {q.q}</p>
                  <div className="space-y-1.5">
                    {q.options.map((opt, idx) => {
                      const selected = answers[i] === String(idx);
                      let cls = "border-white/10 bg-white/[0.02] hover:border-emerald-400/40";
                      if (result && fb) {
                        if (idx === fb.correct_index) cls = "border-emerald-400/60 bg-emerald-400/10";
                        else if (selected) cls = "border-red-400/60 bg-red-400/10";
                      } else if (selected) {
                        cls = "border-emerald-400/60 bg-emerald-400/10";
                      }
                      return (
                        <button
                          key={idx} disabled={!!result}
                          onClick={() => setAnswers((a) => ({ ...a, [i]: String(idx) }))}
                          data-testid={`servicenow-topic-quiz-q-${i}-option-${idx}`}
                          className={`w-full text-left text-sm px-3.5 py-2.5 rounded-lg border transition-colors text-slate-200 flex items-center gap-2 ${cls}`}
                        >
                          {result && fb && idx === fb.correct_index && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />}
                          {result && fb && selected && idx !== fb.correct_index && <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
                          <span>{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {!result ? (
            <button
              onClick={submitQuiz} disabled={!allAnswered || submitting} data-testid="servicenow-topic-quiz-submit-button"
              className="mt-5 w-full sm:w-auto px-6 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />} Submit Quiz
            </button>
          ) : (
            <div
              className={`mt-5 rounded-2xl p-5 text-center border ${result.passed ? "border-emerald-400/40 bg-emerald-400/5" : "border-red-400/40 bg-red-400/5"}`}
              data-testid="servicenow-topic-quiz-result-panel"
            >
              {result.passed ? <CheckCircle2 className="w-7 h-7 text-emerald-400 mx-auto mb-2" /> : <XCircle className="w-7 h-7 text-red-400 mx-auto mb-2" />}
              <p className="text-sm font-semibold text-white">{result.passed ? "Topic Completed!" : "Not quite — review and try again"}</p>
              <p className="text-xs text-slate-400 mt-1">
                Score: {result.score}% ({result.correct_count}/{result.total}) {result.xp_awarded > 0 && `· +${result.xp_awarded} XP earned`}
                {result.already_completed && result.xp_awarded === 0 && " · already completed earlier"}
              </p>
              <div className="flex items-center justify-center gap-3 mt-3">
                {!result.passed && (
                  <button onClick={() => { setResult(null); setAnswers({}); }} data-testid="servicenow-topic-quiz-retry-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
                    Retry Quiz
                  </button>
                )}
                <button onClick={() => navigate("/servicenow-curriculum")} data-testid="servicenow-topic-back-to-map-button" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white transition-colors">
                  Back to Curriculum Map
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
